import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const speculativeDecodingComponent: ComponentDef = {
  id: 'speculative-decoding',
  name: 'Speculative Decoding',
  description: 'Draft model generates γ candidate tokens; target model verifies all in one parallel forward pass via acceptance sampling.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Context length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'draft_tokens', label: 'Draft tokens (γ)', type: 'number', default: 4, min: 1, max: 16, step: 1 },
    {
      name: 'method',
      label: 'Method',
      type: 'select',
      default: 'standard',
      options: [
        { label: 'Standard (separate draft model)', value: 'standard' },
        { label: 'Medusa (parallel draft heads + tree)', value: 'medusa' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const draft_tokens = Number(params.draft_tokens);
    const method = String(params.method || 'standard');
    const d_draft = Math.max(64, Math.floor(d_model / 4));
    const vocab = d_model;

    const g = new GraphBuilder();
    g.input('context', [seq_len, d_model]);

    if (method === 'medusa') {
      // Medusa: target model's hidden states feed K parallel draft heads
      // Each head predicts one future token position; tree attention verifies candidates
      g.linear('backbone', 'context', [d_model, d_model]);
      g.norm('backbone_norm', 'backbone', 'RMSNorm');

      // K parallel Medusa heads (here shown for draft_tokens positions)
      const headIds: string[] = [];
      for (let i = 0; i < draft_tokens; i++) {
        const h = `medusa_head_${i}`;
        g.linear(h, 'backbone_norm', [d_model, vocab]);
        headIds.push(h);
      }
      g.concat('draft_tree', headIds, [draft_tokens, vocab]);
      g.softmax('draft_tree_probs', 'draft_tree', -1);

      // Tree attention: verify all tree candidates in a single forward pass
      g.concat('tree_ctx', ['context', 'draft_tree_probs'], [seq_len + draft_tokens, d_model]);
      g.mask('tree_mask', 'tree_ctx');   // tree-structured causal mask
      g.linear('target_verify', 'tree_mask', [d_model, vocab]);
      g.softmax('target_probs', 'target_verify', -1);

      // Accept longest prefix of tree matching target distribution
      g.activation('accepted', 'target_probs', 'greedy tree traversal: longest accepted prefix');
      g.output('out', 'accepted');

    } else {
      // Standard speculative decoding
      // ── Draft model: small model, generates γ tokens auto-regressively ──
      g.linear('draft_proj', 'context', [d_model, d_draft]);
      g.norm('draft_norm', 'draft_proj', 'RMSNorm');
      g.linear('draft_attn', 'draft_norm', [d_draft, d_draft]);
      g.linear('draft_lm', 'draft_attn', [d_draft, vocab]);
      g.softmax('draft_probs', 'draft_lm', -1);               // p_draft[γ, V]
      g.reshape('draft_tokens_out', 'draft_probs', [draft_tokens, d_model]);

      // ── Target model: one parallel forward on context + γ draft tokens ──
      g.concat('extended_ctx', ['context', 'draft_tokens_out'], [seq_len + draft_tokens, d_model]);
      g.linear('target_attn', 'extended_ctx', [d_model, d_model]);
      g.linear('target_lm', 'target_attn', [d_model, vocab]);
      g.softmax('target_probs', 'target_lm', -1);             // p_target[γ+1, V]

      // ── Acceptance sampling: token-level probability ratio test ──
      // Accept token x_i if u ~ U(0,1) < p_target(x_i) / p_draft(x_i)
      g.activation('accept_ratio', 'target_probs', 'r_i = p_target(x_i) / p_draft(x_i)');
      g.activation('accept_mask', 'accept_ratio', 'accept if U(0,1) < min(1, r_i)');

      // At first rejection: sample bonus token from adjusted distribution max(0, p_t − p_d)
      g.activation('bonus_token', 'accept_mask', 'sample from max(0, p_target − p_draft)');
      g.add('final_tokens', 'accept_mask', 'bonus_token');
      g.output('out', 'final_tokens');
    }

    return g.build();
  }
};
